import { useState } from "react";
import { flowmeterRealtimeData } from "@/mocks/commercialDetail";

interface Props {
  userId: string;
}

export default function FlowmeterPanel({ userId }: Props) {
  const [activeMetric, setActiveMetric] = useState<"flow" | "pressure" | "temperature">("flow");
  const data = flowmeterRealtimeData[userId as keyof typeof flowmeterRealtimeData];

  if (!data) return null;

  const hours = Array.from({ length: 24 }, (_, i) => `${i}:00`);

  const metricConfig = {
    flow: { data: data.hourlyFlow, label: "流量 (m³/h)", color: "#E8531A", unit: "m³/h" },
    pressure: { data: data.pressureHistory.concat(data.pressureHistory), label: "压力 (MPa)", color: "#10b981", unit: "MPa" },
    temperature: { data: data.temperatureHistory.concat(data.temperatureHistory), label: "温度 (°C)", color: "#f59e0b", unit: "°C" },
  };

  const current = metricConfig[activeMetric];
  const chartData = current.data.slice(0, 24);
  const maxVal = Math.max(...chartData) * 1.15;
  const minVal = Math.min(...chartData) * 0.9;

  const W = 700;
  const H = 180;
  const padL = 50;
  const padR = 15;
  const padT = 15;
  const padB = 30;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;

  const toX = (i: number) => padL + (i / (chartData.length - 1)) * chartW;
  const toY = (v: number) => padT + chartH - ((v - minVal) / (maxVal - minVal)) * chartH;

  const pathD = chartData.map((v, i) => `${i === 0 ? "M" : "L"} ${toX(i)} ${toY(v)}`).join(" ");
  const areaD = `${pathD} L ${toX(chartData.length - 1)} ${padT + chartH} L ${toX(0)} ${padT + chartH} Z`;

  const yTicks = 4;
  const yStep = (maxVal - minVal) / yTicks;

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-bold text-gray-900">流量计实时数据</h3>
          <p className="text-xs text-gray-400 mt-0.5">{data.model} · {data.deviceId}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${data.status === "正常" ? "bg-green-500" : "bg-red-500"} animate-pulse`}></div>
          <span className="text-xs text-green-600 font-medium">{data.status}</span>
          <span className="text-xs text-gray-400">更新: {data.lastUpdate.split(" ")[1]}</span>
        </div>
      </div>

      {/* Real-time Stats */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        <div className="bg-orange-50 rounded-xl p-3 text-center">
          <p className="text-xs text-gray-400 mb-1">当前流量</p>
          <p className="text-xl font-bold text-[#E8531A]">{data.currentFlow}</p>
          <p className="text-xs text-gray-400">m³/h</p>
        </div>
        <div className="bg-emerald-50 rounded-xl p-3 text-center">
          <p className="text-xs text-gray-400 mb-1">当前压力</p>
          <p className="text-xl font-bold text-emerald-600">{data.pressure}</p>
          <p className="text-xs text-gray-400">MPa</p>
        </div>
        <div className="bg-amber-50 rounded-xl p-3 text-center">
          <p className="text-xs text-gray-400 mb-1">当前温度</p>
          <p className="text-xl font-bold text-amber-600">{data.temperature}</p>
          <p className="text-xs text-gray-400">°C</p>
        </div>
        <div className="bg-gray-50 rounded-xl p-3 text-center">
          <p className="text-xs text-gray-400 mb-1">信号强度</p>
          <p className="text-xl font-bold text-gray-700">{data.signalStrength}%</p>
          <p className="text-xs text-gray-400">RSSI</p>
        </div>
      </div>

      {/* Daily / Monthly */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div>
            <p className="text-xs text-gray-400">今日累计</p>
            <p className="text-sm font-bold text-gray-800">{data.dailyTotal.toLocaleString()} m³</p>
          </div>
          <div className="w-8 h-8 flex items-center justify-center">
            <i className="ri-calendar-2-line text-gray-400 text-lg"></i>
          </div>
        </div>
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div>
            <p className="text-xs text-gray-400">本月累计</p>
            <p className="text-sm font-bold text-gray-800">{data.monthlyTotal.toLocaleString()} m³</p>
          </div>
          <div className="w-8 h-8 flex items-center justify-center">
            <i className="ri-calendar-line text-gray-400 text-lg"></i>
          </div>
        </div>
      </div>

      {/* Metric Selector */}
      <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 mb-3 w-fit">
        {(["flow", "pressure", "temperature"] as const).map(m => (
          <button
            key={m}
            onClick={() => setActiveMetric(m)}
            className={`px-3 py-1.5 text-xs rounded-md transition-colors cursor-pointer whitespace-nowrap ${
              activeMetric === m ? "bg-white text-gray-900 font-medium" : "text-gray-500"
            }`}
          >
            {m === "flow" ? "流量曲线" : m === "pressure" ? "压力曲线" : "温度曲线"}
          </button>
        ))}
      </div>

      {/* Chart */}
      <div className="overflow-x-auto">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ minWidth: 480 }}>
          <defs>
            <linearGradient id={`grad-${activeMetric}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={current.color} stopOpacity="0.2" />
              <stop offset="100%" stopColor={current.color} stopOpacity="0.01" />
            </linearGradient>
          </defs>
          {Array.from({ length: yTicks + 1 }, (_, i) => {
            const v = minVal + yStep * i;
            const y = toY(v);
            return (
              <g key={i}>
                <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="#f1f5f9" strokeWidth="1" />
                <text x={padL - 5} y={y + 4} textAnchor="end" fontSize="9" fill="#94a3b8">
                  {v.toFixed(activeMetric === "pressure" ? 2 : 0)}
                </text>
              </g>
            );
          })}
          {[0, 4, 8, 12, 16, 20, 23].map(i => (
            <text key={i} x={toX(i)} y={H - 5} textAnchor="middle" fontSize="9" fill="#94a3b8">
              {hours[i]}
            </text>
          ))}
          <path d={areaD} fill={`url(#grad-${activeMetric})`} />
          <path d={pathD} fill="none" stroke={current.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          {chartData.map((v, i) => (
            <circle key={i} cx={toX(i)} cy={toY(v)} r="2.5" fill="white" stroke={current.color} strokeWidth="1.5" />
          ))}
        </svg>
      </div>

      {/* Alarm Status */}
      <div className="mt-3 flex items-center gap-2 p-3 bg-green-50 rounded-lg">
        <div className="w-6 h-6 flex items-center justify-center">
          <i className="ri-shield-check-line text-green-600"></i>
        </div>
        <span className="text-xs text-green-700 font-medium">告警状态：{data.alarmStatus}</span>
        <span className="text-xs text-gray-400 ml-auto">电量：{data.batteryLevel}%</span>
      </div>
    </div>
  );
}
